<template>
    <p
        :class="readOnly && 'readonly'"
        @click="handleClickContent($event)"
        v-html="props.content"
    ></p>
    <div :class="['pt-tooltip-wrap', readOnly && isHoverButton && 'hover']">
        <span v-if="disabledTip" class="tooltip">
            <i>{{ disabledTip }}</i>
        </span>
        <span
            ref="tooltipButton"
            style="
                 {
                    display: inline-block;
                }
            "
        >
            <pt-button
                color="lightblue"
                :type="props.step.done ? 'outline' : 'contain'"
                :disabled="readOnly"
                @click="handleCreateExperience()"
            >
                {{ props.buttonText }}
            </pt-button>
        </span>
    </div>
</template>

<script lang="ts" setup>
import { ref, computed, onMounted, onUnmounted } from 'vue';
import { useCreateDataStore } from '@experience/store/experience.create.store';
import { useGuideStore, GuideStep } from '@/pages/master/store/guide.store';
import { useProfileInfo, Product } from '@models/profile/useCurrentProfile';
import type { Profile } from '@models/profile/profileList';
import useShowAuthDialog from '@master/hooks/service/useShowAuthDialog';
import { verifyPtxPackage } from '@models/package/package';
import useEventBus from '@assets/plugins/eventBus';
import { useI18n } from 'vue-i18n';
import { storeToRefs } from 'pinia';

interface Props {
    roleKey: string;
    type: 'ABTEST' | 'INLINE' | 'POPUP';
    step: GuideStep;
    content: string;
    buttonText: string;
    componentName: 'ExperienceABTestTypeSelect' | 'ExperienceTemplate';
    templateTitle: string;
}
const props = defineProps<Props>();

const { eventBus } = useEventBus();
const { t } = useI18n();
const { updateExperienceCreateData, createExperienceHandler } = useCreateDataStore();
const { createDialogVisible } = storeToRefs(useCreateDataStore());
const { hideGuidePopover, trackEvent } = useGuideStore();
const {
    profileInfo,
    experienceProduct,
    profileProductMenuList,
    validateModule,
    setProfileProductMenu
} = useProfileInfo();
const { checkAuth } = useShowAuthDialog();
const isHoverButton = ref(false);
const tooltipButton = ref();

const readOnly = computed(() => {
    return !validateModule('experience', props.roleKey as any);
});

// 是否布玛
const isActived = computed(() => {
    return profileInfo.value?.isActived === 1;
});

const disabledTip = computed(() => {
    return readOnly.value
        ? t('experience.message.no_create_access_tips', {
              owner: profileInfo.value?.ownerEmail
          })
        : '';
});

const experienceMenu = computed(() => {
    return profileProductMenuList.value?.experience?.find(item => item.key === 'CAMPAIGN');
});

onMounted(() => {
    tooltipButton.value?.addEventListener('mouseenter', handleMouseEnter, false);
    tooltipButton.value?.addEventListener('mouseleave', handleMouseLeave, false);
});

onUnmounted(() => {
    tooltipButton.value?.removeEventListener('mouseenter', handleMouseEnter, false);
    tooltipButton.value?.removeEventListener('mouseleave', handleMouseLeave, false);
});

/**
 * 按类型创建experience
 * @params  isRedirectType 是否是重定向类型
 */
function handleCreateExperience(isRedirectType = false) {
    if (readOnly.value) {
        return;
    }

    eventBus.$emit('hideVerifyDialog');
    eventBus.$emit('hideSetup');

    if (!isActived.value && props.type === 'INLINE') {
        // 未布玛的弹出布玛提示框
        eventBus.$emit('showTrackCodeDialog', 'startGuide_inline');
        hideGuidePopover();
        return;
    }

    const eventNames = {
        POPUP: 'guide_popup_create_click',
        INLINE: 'guide_inline_create_click',
        ABTEST: 'guide_abtest_click'
    };
    trackEvent(eventNames[props.type], {
        has_completed: props.step.done ? 1 : 0
    });

    checkAuth(experienceProduct.value as Product, profileInfo.value as Profile, () => {
        verifyPtxPackage(
            'campaigns',
            async () => {
                props.type === 'ABTEST'
                    ? verifyPtxPackage(
                          'useCampaignAbTest', // 老套餐free 没有创建ab测的权限
                          async () => {
                              await successCallback();
                          },
                          null,
                          !!profileInfo.value?.isNpPackage
                      )
                    : await successCallback();
            },
            null,
            !!profileInfo.value?.isNpPackage
        );
    });

    async function successCallback() {
        // 打开popup模板弹框
        const data = isRedirectType
            ? {
                  entryItem: {
                      key: 'REDIRECT'
                  },
                  entryPosition: 'start_guide',
                  entryComponent: '',
                  experienceType: 'REDIRECT'
              }
            : {
                  visible: true,
                  entryItem: {
                      key: props.type,
                      templateTitle: props.templateTitle
                  },
                  entryPosition: 'start_guide',
                  entryComponent: props.componentName,
                  fullscreenDialog: {
                      component: props.componentName,
                      componentData: {
                          visibleBack: false
                      }
                  }
              };
        if (createDialogVisible) {
            // 如果当前已经打开了其他类型的弹窗步骤，则清空该类型的二步弹框.
            data.dialog = {};
        }
        updateExperienceCreateData(data);
        if (isRedirectType) {
            await createExperienceHandler();
            eventBus.$emit('changeProfileMenuItem', experienceMenu.value);
            setProfileProductMenu(experienceMenu.value);
        }
    }
    hideGuidePopover();
}

/**
 * 给内容里的文字链绑定点击事件
 */
function handleClickContent(e: Event) {
    if (readOnly.value) {
        return;
    }

    // 点击创建重定向测试
    if ((e.target as HTMLElement)?.classList?.contains('js-create-redirect')) {
        handleCreateExperience(true);
    }
}

function handleMouseEnter() {
    isHoverButton.value = true;
}

function handleMouseLeave() {
    isHoverButton.value = false;
}
</script>

<style lang="scss" module>
@import '@style/import.scss';
</style>
